import { Injectable } from '@angular/core';
import { CustomEmoji } from '../models/bot/guild';
import {
  DiscordEmoji,
  EmbedField,
} from '../components/embed-builder/embed.model';
import { EmojiParserService } from 'src/app/services/emojiParser.service';
import { EmbedService } from './embed.service';

@Injectable({
  providedIn: 'root',
})
export class EmojiComponentService {
  constructor(private emojiParser: EmojiParserService) {}

  signupTemplateEmojisToEmbedFields(
    embedService: EmbedService,
    serverCustomEmojis: CustomEmoji[],
    embedFields: { currEmbed?: any },
    emojiInfo: {
      emojis: any;
      emojiBehavior: string;
      useButtons: boolean;
      color: string;
      emojiTitle: boolean;
    },
    selectedMembersMap?: any, // <-- optional map: controlName -> member display names
    serverMembers?: { user_id: string; display_name: string }[]
  ): void {
    const roleFields: EmbedField[] = [];
    const statusFields: EmbedField[] = [];
    const msgEmojis: DiscordEmoji[] = []; // Holds the emojis displayed at the bottom of the preview
    let hasStatusField = false;
    let hasRoleField = false;

    for (const mainEmoji of emojiInfo.emojis) {
      if (!mainEmoji.emoji) continue;

      let value = '';
      const displayEmoji = this.emojiParser?.isCustomEmoji(mainEmoji.emoji)
        ? this.emojiParser.parseCustomEmojiForEmbed(
            mainEmoji.emoji,
            serverCustomEmojis
          )
        : mainEmoji.emoji;

      // Only display top level emojis on 2 and 3 and
      // on 1 if sub emojis are present
      if (!(mainEmoji.subEmojis.length && emojiInfo.emojiBehavior === '1')) {
        msgEmojis.push(
          this.emojiParser.parseEmojiForReaction(
            mainEmoji.emoji,
            serverCustomEmojis,
            mainEmoji.fieldName,
            mainEmoji.type
          )
        );
        const selectedMain = selectedMembersMap?.[mainEmoji.controlName];
        if (selectedMain?.length >= 0) {
          value = selectedMain
            .map((userMap) => {
              const user = serverMembers.find(
                (m) => m.user_id === userMap.user_id
              );
              return user
                ? `${displayEmoji} ${user.display_name}`
                : `${displayEmoji} Unknown`;
            })
            .join('\n');
        } else if (selectedMembersMap && mainEmoji.controlName && selectedMain === undefined) {
          value = ``;
        }
        else {
          value = `${displayEmoji} - ${mainEmoji.fieldName}\n`;
        }
      }

      const subEmojis = mainEmoji.subEmojis;
      if (subEmojis) {
        for (const subEmoji of subEmojis) {
          if (emojiInfo.emojiBehavior === '1') {
            msgEmojis.push(
              this.emojiParser.parseEmojiForReaction(
                subEmoji.emoji,
                serverCustomEmojis,
                `${mainEmoji.fieldName}, ${subEmoji.fieldName}`,
                mainEmoji.type
              )
            );
          }
          const displaySubEmoji = this.emojiParser?.isCustomEmoji(subEmoji.emoji)
            ? this.emojiParser.parseCustomEmojiForEmbed(
                subEmoji.emoji,
                serverCustomEmojis
              ) || subEmoji.emoji
            : subEmoji.emoji;
          const selectedSub = selectedMembersMap?.[subEmoji.controlName];
          if (selectedSub?.length > 0) {
            value +=
              selectedSub
                .map((userMap) => {
                  const user = serverMembers.find(
                    (m) => m.user_id === userMap.user_id
                  );
                  return user
                    ? `${displaySubEmoji} ${user.display_name}`
                    : `${displaySubEmoji} Unknown`;
                })
                .join('\n') + '\n';
          } else {
            value += `${displaySubEmoji} - ${mainEmoji.fieldName}, ${subEmoji.fieldName}\n`;
          }
        }
      }

      let memberCount = selectedMembersMap?.[mainEmoji.controlName]?.length || 0;
      for (const subEmoji of mainEmoji.subEmojis || []) {
        memberCount += selectedMembersMap?.[subEmoji.controlName]?.length || 0;
      }
      const limit = Number(mainEmoji.limit) || 0;
      let countLabel = '';
      if (selectedMembersMap) {
        countLabel = limit > 0 ? ` (${memberCount}/${limit})` : ` (${memberCount})`;
      } else if (limit > 0) {
        countLabel = ` (0/${limit})`;
      }
      const name = this.emojiParser.replaceCustomEmojis(
        emojiInfo.emojiTitle
          ? `${displayEmoji} ${mainEmoji.fieldName}${countLabel}`
          : `${mainEmoji.fieldName}${countLabel}`
      );
      const renderedValue = this.emojiParser.replaceCustomEmojis(value.trim());
      if (mainEmoji.type === 'status') {
        statusFields.push({
          name: name,
          value: renderedValue,
          inline: true,
        });
        hasStatusField = true;
      } else if (mainEmoji.type === 'role') {
        roleFields.push({
          name: name,
          value: renderedValue,
          inline: true,
        });
        hasRoleField = true;
      }
    }

    const targetEmbedFields = embedFields.currEmbed; // || embedFields.signupConfg; TODO: check what this config was.
    targetEmbedFields.fields = roleFields;

    if (hasStatusField) {
      if (hasRoleField) {
        targetEmbedFields.fields.push({
          name: '',
          value: '---',
          inline: false,
        });
      }
      targetEmbedFields.fields.push(...statusFields);
    }

    targetEmbedFields.color = emojiInfo?.color ?? '#000000';

    embedService.updateEmbed(targetEmbedFields);
    embedService.updateEmojis(msgEmojis);
    embedService.updateSignupTemplateUseButtons(emojiInfo?.useButtons);
  }
}
